import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Icons, IconButton, Spinner, cx } from '../../components/ui.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import { formatBytes, formatRelative } from '../../lib/display.ts';
import { GuestRecordViewer, type GuestRecord } from './GuestRecordViewer.tsx';

/**
 * A shared folder, for somebody with no account.
 *
 * The same idea as the shared-file page and a different shape: a file is one
 * thing to look at, a folder is a place to work in. So this is a small Drive —
 * breadcrumbs, a listing, a preview — with exactly the controls the link
 * allows and not one more.
 *
 * What it will not do is as deliberate as what it will. There is no sign-in
 * prompt, no search across the system, no way up out of the shared folder, and
 * no sharing: the guest was given one room, and the page is that room. Every
 * button here maps to a route that checks the same thing again on the server —
 * this page is a convenience, never the control.
 */

type Access = 'VIEW' | 'EDIT' | 'DELETE';

interface Listing {
  access: Access;
  allowDownload: boolean;
  root: string;
  folder: { id: string; name: string; path: string };
  breadcrumbs: { id: string; name: string }[];
  folders: { id: string; name: string; updatedAt: string }[];
  files: { id: string; name: string; mimeType: string; sizeBytes: number; updatedAt: string }[];
  documents: { id: string; name: string; updatedAt: string }[];
  spreadsheets: { id: string; name: string; updatedAt: string }[];
}

interface Viewing {
  id: string;
  name: string;
  /** What the browser will be handed: the file's own type, or PDF. */
  mimeType: string;
  kind: 'file' | 'document' | 'spreadsheet';
}

const may = (access: Access, needed: Access) =>
  ['VIEW', 'EDIT', 'DELETE'].indexOf(access) >= ['VIEW', 'EDIT', 'DELETE'].indexOf(needed);

export function SharedFolderPage({ token }: { token: string }) {
  const [listing, setListing] = useState<Listing | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [at, setAt] = useState<string | null>(null);
  /** An uploaded file, open full screen. */
  const [viewing, setViewing] = useState<Viewing | null>(null);
  /** A sheet or a letter, open in the editor it belongs in. */
  const [reading, setReading] = useState<GuestRecord | null>(null);
  const upload = useRef<HTMLInputElement | null>(null);

  const base = `/api/public/${encodeURIComponent(token)}/folder`;

  const load = useCallback(
    async (folderId: string | null) => {
      try {
        const query = folderId ? `?folderId=${encodeURIComponent(folderId)}` : '';
        const response = await fetch(`${base}${query}`, { credentials: 'omit' });
        if (!response.ok) throw new Error('gone');
        setListing((await response.json()) as Listing);
      } catch {
        setFailed(true);
      }
    },
    [base],
  );

  useEffect(() => {
    void load(at);
  }, [load, at]);

  /** One write, then the listing again — the server is the record, not this. */
  const act = async (path: string, body: unknown, label: string) => {
    setBusy(label);
    try {
      const response = await fetch(`${base}/${path}`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const detail = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(detail?.message ?? 'That did not work.');
      }
      await load(at);
    } catch (caught) {
      window.alert(caught instanceof Error ? caught.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  const send = async (file: File) => {
    setBusy('upload');
    try {
      const form = new FormData();
      form.append('file', file);
      const query = at ? `?folderId=${encodeURIComponent(at)}` : '';
      const response = await fetch(`${base}/upload${query}`, {
        method: 'POST',
        credentials: 'omit',
        body: form,
      });
      if (!response.ok) throw new Error('That file could not be uploaded.');
      await load(at);
    } catch (caught) {
      window.alert(caught instanceof Error ? caught.message : 'That file could not be uploaded.');
    } finally {
      setBusy(null);
    }
  };

  if (failed) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-canvas px-6 text-center">
        <h1 className="text-h2 text-ink">This link is no longer available</h1>
        <p className="mt-1 max-w-md text-body-sm text-muted">
          It may have been turned off by the person who shared it, or it may have expired. Ask them
          for a new one.
        </p>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas">
        <Spinner label="Opening" />
      </div>
    );
  }

  const mayEdit = may(listing.access, 'EDIT');
  const mayDelete = may(listing.access, 'DELETE');
  const empty =
    listing.folders.length === 0 &&
    listing.files.length === 0 &&
    listing.documents.length === 0 &&
    listing.spreadsheets.length === 0;

  return (
    <div className="flex h-screen flex-col bg-canvas">
      <header className="shrink-0 border-b border-border-strong bg-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <BrandIcon src={BRAND.folder} size={22} />

          {/* Only ever from the shared folder down. There is no way up from
              here, because up is somebody else's Drive. */}
          <nav className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-body-sm">
            {listing.breadcrumbs.map((crumb, index) => (
              <span key={crumb.id} className="flex items-center gap-1">
                {index > 0 ? <span className="text-subtle">/</span> : null}
                <button
                  type="button"
                  onClick={() => setAt(index === 0 ? null : crumb.id)}
                  className={cx(
                    'truncate rounded px-1 py-0.5 hover:bg-sunken',
                    index === listing.breadcrumbs.length - 1
                      ? 'font-medium text-ink'
                      : 'text-muted',
                  )}
                >
                  {crumb.name}
                </button>
              </span>
            ))}
          </nav>

          <span className="rounded-full border border-border px-2.5 py-0.5 text-micro text-muted">
            {listing.access === 'VIEW'
              ? 'You can view'
              : listing.access === 'EDIT'
                ? 'You can add and rename'
                : 'You can add, rename and remove'}
          </span>

          {mayEdit ? (
            <>
              <input
                ref={upload}
                type="file"
                className="hidden"
                onChange={(event) => {
                  const chosen = event.target.files?.[0];
                  event.target.value = '';
                  if (chosen) void send(chosen);
                }}
              />
              <Button
                icon={<Icons.Upload />}
                onClick={() => upload.current?.click()}
                disabled={busy !== null}
              >
                {busy === 'upload' ? 'Uploading…' : 'Upload'}
              </Button>
              <Button
                icon={<Icons.Folder />}
                onClick={() => {
                  const name = window.prompt('Name the new folder');
                  if (name?.trim()) {
                    void act('folders', { parentId: at, name: name.trim() }, 'folder');
                  }
                }}
                disabled={busy !== null}
              >
                New folder
              </Button>
            </>
          ) : null}
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto p-4">
        {empty ? (
          <p className="py-16 text-center text-body-sm text-muted">
            This folder is empty.
            {mayEdit ? ' Use Upload to put something in it.' : ''}
          </p>
        ) : (
          <ul className="overflow-hidden rounded-lg border border-border-strong bg-surface">
            {listing.folders.map((folder) => (
              <Row
                key={folder.id}
                icon={<BrandIcon src={BRAND.folder} size={20} />}
                name={folder.name}
                detail={formatRelative(folder.updatedAt)}
                onOpen={() => setAt(folder.id)}
                {...(mayEdit
                  ? {
                      onRename: () => {
                        const name = window.prompt('Rename this folder', folder.name);
                        if (name?.trim()) {
                          void act(
                            'rename',
                            { kind: 'folder', id: folder.id, name: name.trim() },
                            'rename',
                          );
                        }
                      },
                    }
                  : {})}
                {...(mayDelete
                  ? {
                      onTrash: () => {
                        if (
                          window.confirm(`Move "${folder.name}" and everything in it to the trash?`)
                        ) {
                          void act('trash', { kind: 'folder', id: folder.id }, 'trash');
                        }
                      },
                    }
                  : {})}
              />
            ))}

            {listing.files.map((file) => (
              <Row
                key={file.id}
                icon={<FileTypeIcon name={file.name} mimeType={file.mimeType} />}
                name={file.name}
                detail={`${formatBytes(file.sizeBytes)} · ${formatRelative(file.updatedAt)}`}
                onOpen={() => setViewing({ ...file, kind: 'file' })}
                {...(listing.allowDownload
                  ? { downloadHref: `${base}/open/file/${file.id}?download=1` }
                  : {})}
                {...(mayEdit
                  ? {
                      onRename: () => {
                        const name = window.prompt('Rename this file', file.name);
                        if (name?.trim()) {
                          void act(
                            'rename',
                            { kind: 'file', id: file.id, name: name.trim() },
                            'rename',
                          );
                        }
                      },
                    }
                  : {})}
                {...(mayDelete
                  ? {
                      onTrash: () => {
                        if (window.confirm(`Move "${file.name}" to the trash?`)) {
                          void act('trash', { kind: 'file', id: file.id }, 'trash');
                        }
                      },
                    }
                  : {})}
              />
            ))}

            {/*
              Sheets and letters, which are not files.

              They live in our own tables and are edited in an editor that
              needs an account, so what a guest gets is the PDF each one prints
              to — the paperwork as it stands, which is what somebody sent a
              link actually wants. Renaming and binning are the same acts on
              the same ladder as a file's.
            */}
            {[
              ...listing.spreadsheets.map((item) => ({ ...item, kind: 'spreadsheet' as const })),
              ...listing.documents.map((item) => ({ ...item, kind: 'document' as const })),
            ].map((item) => (
              <Row
                key={item.id}
                icon={
                  <BrandIcon
                    src={item.kind === 'spreadsheet' ? BRAND.excel : BRAND.word}
                    size={20}
                  />
                }
                name={item.name}
                detail={formatRelative(item.updatedAt)}
                onOpen={() => setReading({ id: item.id, kind: item.kind, name: item.name })}
                {...(listing.allowDownload
                  ? { downloadHref: `${base}/open/${item.kind}/${item.id}?download=1` }
                  : {})}
                {...(mayEdit
                  ? {
                      onRename: () => {
                        const name = window.prompt(
                          item.kind === 'spreadsheet' ? 'Rename this sheet' : 'Rename this letter',
                          item.name,
                        );
                        if (name?.trim()) {
                          void act(
                            'rename',
                            { kind: item.kind, id: item.id, name: name.trim() },
                            'rename',
                          );
                        }
                      },
                    }
                  : {})}
                {...(mayDelete
                  ? {
                      onTrash: () => {
                        if (window.confirm(`Move "${item.name}" to the trash?`)) {
                          void act('trash', { kind: item.kind, id: item.id }, 'trash');
                        }
                      },
                    }
                  : {})}
              />
            ))}
          </ul>
        )}
      </main>

      {reading ? (
        <GuestRecordViewer
          src={`${base}/record/${reading.kind}/${reading.id}`}
          downloadHref={
            listing.allowDownload
              ? `${base}/open/${reading.kind}/${reading.id}?download=1`
              : undefined
          }
          name={reading.name}
          kind={reading.kind}
          onClose={() => setReading(null)}
        />
      ) : null}

      {viewing ? (
        <GuestViewer
          base={base}
          file={viewing}
          allowDownload={listing.allowDownload}
          onClose={() => setViewing(null)}
        />
      ) : null}

      <footer className="shrink-0 border-t border-border px-4 py-2 text-center text-micro text-muted">
        Shared from the Abdul Qadir Finance System. Anyone with this link can see this folder.
      </footer>
    </div>
  );
}

function Row({
  icon,
  name,
  detail,
  onOpen,
  onRename,
  onTrash,
  downloadHref,
}: {
  icon: React.ReactNode;
  name: string;
  detail: string;
  onOpen?: () => void;
  onRename?: () => void;
  onTrash?: () => void;
  downloadHref?: string;
}) {
  return (
    <li className="group flex items-center gap-3 border-b border-border px-4 py-2.5 transition-fast last:border-0 hover:bg-blue-50">
      <span className="shrink-0" aria-hidden>
        {icon}
      </span>

      <span className="min-w-0 flex-1">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            className="block max-w-full truncate text-left text-body-sm text-ink hover:underline"
          >
            {name}
          </button>
        ) : (
          <span className="block truncate text-body-sm text-ink">{name}</span>
        )}
        <span className="block truncate text-micro text-muted">{detail}</span>
      </span>

      <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-fast group-hover:opacity-100">
        {/* The anchor carries no `download` attribute on purpose: the server
            already names the file in Content-Disposition, and a sheet comes
            down as the PDF it printed to rather than under its own
            extension-less name. */}
        {downloadHref ? (
          <a href={downloadHref}>
            <IconButton label={`Download ${name}`}>
              <Icons.Download />
            </IconButton>
          </a>
        ) : null}
        {onRename ? (
          <IconButton label={`Rename ${name}`} onClick={onRename}>
            <Icons.Pencil />
          </IconButton>
        ) : null}
        {onTrash ? (
          <IconButton label={`Move ${name} to the trash`} onClick={onTrash}>
            <Icons.Trash />
          </IconButton>
        ) : null}
      </span>
    </li>
  );
}

/**
 * One file, full screen.
 *
 * The bytes are fetched and framed as a blob for the same reason the
 * shared-file page does it: the site refuses to let any of its own responses
 * be rendered inside a frame, and a blob is not a response.
 */
function GuestViewer({
  base,
  file,
  allowDownload,
  onClose,
}: {
  base: string;
  file: Viewing;
  allowDownload: boolean;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(`${base}/open/${file.kind}/${file.id}`, {
          credentials: 'omit',
        });
        if (!response.ok) throw new Error('gone');

        objectUrl = URL.createObjectURL(await response.blob());
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [base, file.id, file.kind]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[rgba(9,17,28,0.88)]">
      <header className="flex shrink-0 items-center gap-3 px-4 py-3">
        <FileTypeIcon name={file.name} mimeType={file.mimeType} size={22} />
        <p className="min-w-0 flex-1 truncate text-body-sm font-medium text-white">{file.name}</p>

        {allowDownload ? (
          <a href={`${base}/open/${file.kind}/${file.id}?download=1`}>
            <Button
              icon={<Icons.Download />}
              className="border-white/25 bg-white/10 text-white hover:bg-white/20"
            >
              Download
            </Button>
          </a>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-2 text-white/80 transition-fast hover:bg-white/15 hover:text-white"
        >
          <Icons.Close size={20} />
        </button>
      </header>

      <div className="min-h-0 flex-1 px-4 pb-4">
        <div className="flex h-full items-center justify-center overflow-hidden rounded-xl bg-surface">
          {failed ? (
            <Alert>That file could not be opened.</Alert>
          ) : !url ? (
            <Spinner label="Opening" />
          ) : file.mimeType.startsWith('image/') ? (
            <img src={url} alt={file.name} className="max-h-full max-w-full object-contain" />
          ) : file.mimeType.startsWith('video/') ? (
            <video src={url} controls className="max-h-full max-w-full" />
          ) : (
            <iframe
              src={`${url}#view=Fit&navpanes=0`}
              title={file.name}
              className="h-full w-full border-0"
            />
          )}
        </div>
      </div>
    </div>
  );
}
